import React, {FC, ReactNode} from 'react';
import styled, {css} from 'styled-components';

import {Handler} from '../../../libs/shared/helpers/src/react/reactHelpers';
import {ChromelessButton} from '../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {Icon, IconName} from '../../../libs/shared/ui/src/components/icon/icon';
import {fontSizes, fontWeights} from '../../../libs/shared/ui/src/styles/fontStyles';
import {boxShadow} from '../../../libs/shared/ui/src/theme/themeStyleHelpers';
import {OnboardingGuideSettingsIcon} from './onboardingGuideSettingsIcon';

/*
 * Props.
 */

export interface OnboardingGuideTaskProps {
  buttonLabel?: ReactNode;
  icon: IconName;
  title: ReactNode;
  description: ReactNode;
  isComplete: boolean;
  onClick: Handler;
}

export type OnboardingGuideTaskRequiredProps = Omit<
  OnboardingGuideTaskProps,
  'title' | 'icon' | 'description'
>;

/*
 * Style.
 */

interface PropsWithIsComplete {
  $isComplete: boolean;
}

const StyledChromelessButton = styled(ChromelessButton)<PropsWithIsComplete>`
  border-radius: 16px;
  background: ${(p) => p.theme.foreground};
  ${(p) => boxShadow(p.theme, `0 1px 9px ${p.theme.alphas.black20}`)};
  border: 1px solid ${(p) => p.theme.alphas.black20};
  padding: 16px;

  display: grid;
  grid-template-areas: 'icon . content';
  grid-template-columns: auto 16px 1fr;

  min-width: 300px;
  margin: 0;
  overflow: hidden;

  width: 100%;

  &,
  & * {
    cursor: pointer;
  }
`;

const StyledTaskTitle = styled.h3`
  margin: 0;
  font-size: ${fontSizes.large};
  color: ${(p) => p.theme.greys.shade80};
  font-weight: ${fontWeights.bold};
`;

const StyledTaskDescription = styled.p`
  font-size: ${fontSizes.medium};
  color: ${(p) => p.theme.greys.shade70};
  font-weight: ${fontWeights.normal};
  line-height: 1.4;
  margin-top: 7px;
  margin-bottom: 0;
`;

const StyledContentWrapper = styled.div<PropsWithIsComplete>`
  grid-area: content;
  ${(p) =>
    p.$isComplete &&
    css`
      padding-top: 6px;
    `};

  display: flex;
  flex-direction: column;
`;

const StyledTextWrapper = styled.div`
  flex-grow: 1;
`;

const StyledTaskCircleDiv = styled.div`
  grid-area: checkbox;
  border-radius: 50%;
  border: 1px solid ${(p) => p.theme.greys.shade50};
  width: 22px;
  height: 22px;
  margin-right: 3px;
`;

const StyledIcon = styled(Icon)`
  grid-area: checkbox;
`;

const StyledHeaderContainerDiv = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`;

export const OnboardingGuideTask: FC<OnboardingGuideTaskProps> = (props) => {
  const {onClick, isComplete} = props;
  return (
    <StyledChromelessButton onClick={onClick} $isComplete={isComplete}>
      <OnboardingGuideSettingsIcon icon={props.icon} isComplete={isComplete} />
      <StyledContentWrapper $isComplete={isComplete}>
        <StyledTextWrapper>
          <StyledHeaderContainerDiv>
            <StyledTaskTitle>{props.title}</StyledTaskTitle>
            {maybeRenderTaskCircle(isComplete)}
          </StyledHeaderContainerDiv>
          {!isComplete && <StyledTaskDescription>{props.description}</StyledTaskDescription>}
        </StyledTextWrapper>
      </StyledContentWrapper>
    </StyledChromelessButton>
  );
};

/*
 * Helpers.
 */

function maybeRenderTaskCircle(isComplete: boolean) {
  if (!isComplete) {
    return <StyledTaskCircleDiv />;
  }
  return <StyledIcon name="checkmarkCircleLarge" color="#4A90E2" />;
}
